/**
 * FTUE host (PEL-516).
 *
 * Single mount point (rendered once in the app layout). Reads the resolved
 * active step from the engine and renders the appropriate surface — coachmark
 * (+ spotlight), off-page badge, or toast — into a body portal. Renders nothing
 * when there is no active step or while waiting on an anchor (`surface: none`).
 */

import { type ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useFtue } from '../use-ftue'
import { Coachmark } from './Coachmark'
import { FtueBadge } from './FtueBadge'
import { FtueToast } from './FtueToast'
import { Spotlight } from './Spotlight'

export function FtueHost() {
  const active = useFtue()

  // Diagnosis aid (spec §7): expose the resolved step + anchor element so a wrong
  // or stale anchor is visible from the console (`window.__ftue`). Keyed on the
  // step/element/surface so it doesn't churn on the per-frame `active` identity.
  const stepId = active?.step.id ?? null
  const element = active?.element ?? null
  const surface = active?.surface ?? null
  useEffect(() => {
    if (!import.meta.env.DEV) return
    ;(window as unknown as { __ftue?: unknown }).__ftue = active
      ? {
          stepId,
          anchorId: active.step.anchorId,
          surface,
          element,
          rect: element?.getBoundingClientRect() ?? null,
        }
      : null
  }, [stepId, element, surface, active])

  // Flag the document while a modal coaching step is on screen, so portaled Radix
  // popovers (Select/Dropdown/Popover) opened from the highlighted field are lifted
  // above the scrim instead of hiding behind the mask (see index.css). Only set for
  // blocking coachmarks — a non-modal step has no scrim, so nothing to escape.
  const modalActive =
    !!active &&
    active.surface === 'coachmark' &&
    active.element != null &&
    (active.step.modal ?? true)
  useEffect(() => {
    if (!modalActive) return
    const root = document.documentElement
    root.setAttribute('data-ftue-modal', '')
    return () => root.removeAttribute('data-ftue-modal')
  }, [modalActive])

  if (!active || active.surface === 'none') return null

  let node: ReactNode = null
  switch (active.surface) {
    case 'coachmark':
      node = active.element ? (
        <>
          {/* Modal by default (spec §2.2): the scrim blocks interaction except the
              highlight + coachmark. A step can opt out with `modal: false`. The
              Spotlight only mounts here when a coachmark is rendered on a present
              anchor, so waiting/event-gated states never show a scrim. A
              scroll-locked overlay step (e.g. the Match Analysis fit step) also
              clips the cutout to its scroll viewport so the highlight can't bleed
              past the scroll boundary into a pinned footer on a separate layer. */}
          <Spotlight
            element={active.element}
            blocking={active.step.modal ?? true}
            clipToScrollParent={(active.step.lockScroll ?? false) && active.layer === 'overlay'}
            scrollPassthrough={!(active.step.lockScroll ?? false)}
            {...(active.step.spotlightPadding !== undefined
              ? { padding: active.step.spotlightPadding }
              : {})}
          />
          {/* Remount when the anchored element changes — by step AND by layer: a
              reused coachmark keeps floating-ui's autoUpdate bound to the previous
              reference, so the popover would freeze at the old anchor while only the
              spotlight re-resolves. The layer flips when a concept-based step
              re-anchors from the list card to the detail overlay (and back), so it
              must be part of the key. A fresh instance re-binds to the current
              element (and re-types the surface-specific copy). */}
          <Coachmark key={`${active.step.id}:${active.layer}`} active={active} />
        </>
      ) : null
      break
    case 'badge':
      node = <FtueBadge active={active} />
      break
    case 'toast':
      node = <FtueToast active={active} />
      break
  }

  if (!node) return null
  return createPortal(node, document.body)
}
